import React, { CSSProperties, useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { DatasetType } from '@mui/x-charts/internals';
import { BarSeriesType, LineSeriesType } from '@mui/x-charts';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import { UserContext } from '../../../../../context/UserContext';
import AreaChartOutlinedIcon from '../../../../../components/AreaChartOutlinedIcon';
import { formatValue, getUniqueProperties } from '../../../../../helpers/helpers';
import { AvailableMetric, ChartType } from '../../../../../models/dashboard';
import { Bucket } from '../../../../../api/indicators';
import { DashboardFiltersContext } from '../../filters/DashboardFiltersContext';
import LineBarChartSkeleton from './LineBarChartSkeleton';
import LineBarChartError from './LineBarChartError';
import LineBarChartBase from './LineBarChartBase';
import ChartNoData from '../ChartNoData';

const selectStyle: CSSProperties = {
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  width: '210px',
};

export interface LineBarChartDataItem {
  dateTime: Date;
  barData: number;
  [lineData: string]: number | Date;
}

export interface LineBarChartProps {
  title?: string;
  currency?: string;
  description?: string;
  textLink?: string;
  linkChart?: string;
  isLoading: boolean;
  isError: boolean;
  leftAxisLabel: string;
  rightAxisLabel?: string;
  lineLabels?: string[];
  barLabel?: string;
  availableMetrics?: AvailableMetric[];
  selectedMetrics?: string[];
  defaultSelectedMetrics?: string[];
  onSelectedMetricsChange?: (selectedMetrics: string[]) => void;
  maxSelectedMetrics?: number;
  barDataKey?: string;
  allowedBuckets?: Array<'hour' | 'day' | 'week'>;
  data: DatasetType;
  bucket: Bucket;
  setBucket: (bucket: Bucket) => void;
  height: number;
  forceNoData?: boolean;
  hideFiltersButton?: boolean;
}

const LineBarChart = (props: LineBarChartProps) => {
  const {
    title,
    currency,
    description,
    isLoading,
    isError,
    leftAxisLabel,
    rightAxisLabel,
    textLink,
    linkChart,
    lineLabels,
    barLabel,
    availableMetrics,
    selectedMetrics,
    defaultSelectedMetrics,
    onSelectedMetricsChange,
    maxSelectedMetrics,
    barDataKey = 'barData',
    allowedBuckets,
    data,
    bucket,
    setBucket,
    height,
    forceNoData,
    hideFiltersButton
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { locale } = useContext(UserContext);
  const { openFilters } = useContext(DashboardFiltersContext);
  const [chartType, setChartType] = useState<ChartType>('line');
  const [linePlotSeries, setLinePlotSeries] = useState<(LineSeriesType | BarSeriesType)[]>([]);
  const [areaPlotSeries, setAreaPlotSeries] = useState<(LineSeriesType | BarSeriesType)[]>([]);
  const [series, setSeries] = useState<(LineSeriesType | BarSeriesType)[]>([]);
  const [internalSelectedMetrics, setInternalSelectedMetrics] = useState<string[]>(() => {
    if (selectedMetrics && selectedMetrics.length > 0) {
      return selectedMetrics;
    }
    if (defaultSelectedMetrics && defaultSelectedMetrics.length > 0) {
      return defaultSelectedMetrics;
    }
    if (availableMetrics?.length) {
      const defaultMetric = availableMetrics.find((m) => m.key === 'netSalesExcTax');
      return [defaultMetric?.key ?? availableMetrics[0].key];
    }
    return [];
  });

  useEffect(() => {
    setInternalSelectedMetrics(selectedMetrics || []);
  }, [selectedMetrics]);

  const colours: string[] = useMemo(() =>
    [
      theme.palette.blue.main,
      theme.palette.cyan.main,
      theme.palette.teal.main,
      theme.palette.lightGreen.main,
      theme.palette.lime.main,
      theme.palette.yellow.main,
      theme.palette.orange.main,
      theme.palette.deepOrange.main,
      theme.palette.fucsia.main,
      theme.palette.purple.main,
    ], [
    theme.palette.blue.main,
    theme.palette.cyan.main,
    theme.palette.teal.main,
    theme.palette.lightGreen.main,
    theme.palette.lime.main,
    theme.palette.yellow.main,
    theme.palette.orange.main,
    theme.palette.deepOrange.main,
    theme.palette.fucsia.main,
    theme.palette.purple.main,
  ]);
  const noData = !data?.length;

  const handleMetricsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as unknown as string[];
    if (maxSelectedMetrics && value.length > maxSelectedMetrics) {
      return;
    }
    if (onSelectedMetricsChange) {
      onSelectedMetricsChange(value);
    }
    if (selectedMetrics === undefined) {
      setInternalSelectedMetrics(value);
    }
  };

  useEffect(() => {
    if (noData) return;

    const lineKeys = availableMetrics
      ? internalSelectedMetrics
      : getUniqueProperties(data, ['dateTime', barDataKey]);

    const barSeries: BarSeriesType = {
      id: barDataKey,
      label: barLabel,
      type: 'bar',
      dataKey: barDataKey,
      color: theme?.palette?.darkerGrey.light,
      yAxisId: 'y-axis-right-id',
    };

    const getLineLabel = (lineKey: string, index: number) => {
      if (availableMetrics) {
        return availableMetrics.find((metric) => metric.key === lineKey)?.label ?? lineLabels?.[index] ?? lineKey;
      }
      return lineLabels?.[index] ?? lineKey;
    };

    const lineSeries: LineSeriesType[] = lineKeys.map((lineKey, index) => ({
      id: lineKey,
      label: getLineLabel(lineKey, index),
      type: 'line',
      dataKey: lineKey,
      curve: 'linear',
      color: colours[index % colours.length],
      showMark: true,
      markSize: 5,
      valueFormatter: (v: number | null) => formatValue(v, true, currency, locale),
    }));

    const areaSeries: LineSeriesType[] = lineKeys.map((lineKey, index) => ({
      id: lineKey,
      label: getLineLabel(lineKey, index),
      type: 'line',
      dataKey: lineKey,
      curve: 'linear',
      color: colours[index % colours.length],
      valueFormatter: (v: number | null) => formatValue(v, true, currency, locale),
      area: true,
    }));

    const lineBarSeries = [barSeries, ...lineSeries];
    const areaLineBarSeries = [barSeries, ...areaSeries];
    setLinePlotSeries(lineBarSeries);
    setAreaPlotSeries(areaLineBarSeries);

    switch (chartType) {
      case 'line':
        setSeries(lineBarSeries);
        break;
      case 'area':
        setSeries(areaLineBarSeries);
        break;
      default:
        setSeries(lineBarSeries);
        break;
    }
  }, [data, colours, currency, barLabel, lineLabels, locale, theme?.palette?.grey, theme?.palette?.darkerGrey?.light, chartType, noData, availableMetrics, internalSelectedMetrics, barDataKey]);

  const toggleChart = (
    event: React.MouseEvent<HTMLElement>,
    newChartType: ChartType,
  ) => {
    if (newChartType !== null) {
      setChartType(newChartType);
      if (newChartType === 'line') {
        setSeries(linePlotSeries);
      }
      if (newChartType === 'area') {
        setSeries(areaPlotSeries);
      }
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        minWidth: '200px',
        border: `solid 1px`,
        borderColor: theme.palette.grey['12p'],
        marginX: '8px',
        padding: '12px 16px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        breakInside: 'avoid',
        pageBreakInside: 'avoid'
      }}
    >
      {isLoading && <LineBarChartSkeleton />}
      {!isLoading && (
        <>
          {isError && !forceNoData && <LineBarChartError title={title ?? ''} description={description ?? ''} bucket={bucket} setBucket={setBucket} />}
          {(forceNoData || (!isError && noData)) && <ChartNoData height={height} title={title ?? ''} description={description ?? ''} hideFiltersButton={hideFiltersButton} openFilters={openFilters} />}
          {!forceNoData && !isError && !noData && (
            <Box sx={{ minHeight: height }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginY: '6px', gap: 2, alignItems: 'center' }}>
                {(title || description) && (
                  <Box>
                    {title && <Typography variant='h2'>{title}</Typography>}
                    {description && <Typography variant='body2'>{description}</Typography>}
                  </Box>
                )}
                {availableMetrics?.length ? (
                  <Box>
                    <TextField
                      size="medium"
                      sx={selectStyle}
                      select
                      value={internalSelectedMetrics}
                      id="metrics"
                      onChange={handleMetricsChange}
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                          const values = selected as string[];

                          if (values.length === 0) {
                            return <span style={{ color: '#999' }}>{t('screens.dashboarding.charts.noMetricsSelected')}</span>;
                          }

                          return values
                            .map((key) => availableMetrics.find((metric) => metric.key === key)?.label)
                            .join(', ')
                        }
                      }}
                      inputProps={{ 'data-qa-id': 'filterMetricsOption' }}
                    >
                      {availableMetrics.map((metric) => (
                        <MenuItem
                          key={metric.key}
                          value={metric.key}
                          disabled={!internalSelectedMetrics.includes(metric.key) && internalSelectedMetrics.length >= (maxSelectedMetrics ?? 3)}
                        >
                          <Checkbox checked={internalSelectedMetrics.includes(metric.key)} />
                          {metric.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                ) : null}
                <>
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <TextField
                      size="medium"
                      sx={selectStyle}
                      select
                      label={t("screens.dashboarding.charts.showBy")}
                      value={bucket}
                      id="period"
                      onChange={(event) => setBucket(event.target.value as Bucket)}
                      inputProps={{ 'data-qa-id': "filterPeriodOption" }}
                    >
                      {(!allowedBuckets || allowedBuckets.includes('hour')) && <MenuItem value="hour">{t('components.select.hour')}</MenuItem>}
                      {(!allowedBuckets || allowedBuckets.includes('day')) && <MenuItem value="day">{t('components.select.days')}</MenuItem>}
                      {(!allowedBuckets || allowedBuckets.includes('week')) && <MenuItem value="week">{t('components.select.week')}</MenuItem>}
                    </TextField>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto' }}>
                    <ToggleButtonGroup color="primary" value={chartType} exclusive onChange={toggleChart}>
                      <ToggleButton value="line" sx={{ border: 'none', borderRadius: '50% !important', margin: '5px' }}>
                        <StackedLineChartIcon />
                      </ToggleButton>
                      <ToggleButton value="area" sx={{ border: 'none', borderRadius: '50% !important', margin: '5px' }}>
                        <AreaChartOutlinedIcon />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </>
              </Box>
              <>
                <LineBarChartBase bucket={bucket} chartType={chartType} series={series} data={data} height={height} leftAxisLabel={leftAxisLabel} rightAxisLabel={rightAxisLabel} />
                {!!linkChart && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                    <Button href={linkChart} style={theme.typography.pieBarText} sx={{ color: 'primary.main' }}>
                      {textLink}
                    </Button>
                  </Box>
                )}
              </>
            </Box>
          )}
        </>
      )}
    </Card>
  );
}

export default LineBarChart;
